import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import Category from "@/pages/Category";
import About from "@/pages/About";
import Cemeteries from "@/pages/Cemeteries";
import Contact from "@/pages/Contact";
import Region from "@/pages/Region";
import Heritage from "@/pages/Heritage";
import Culture from "@/pages/Culture";
import Genealogy from "@/pages/Genealogy";
import Memorials from "@/pages/Memorials";
import FamilyTrees from "@/pages/FamilyTrees";
import CelebrateALifePage from "@/pages/CelebrateALifePage";
import Archive from "@/pages/Archive";
import PublicMemorial from "@/pages/PublicMemorial";
import Local from "@/pages/Local";
import MissingDirectory from "@/pages/MissingDirectory";
import MissingCase from "@/pages/MissingCase";
import ReportMissing from "@/pages/ReportMissing";
import Disasters from "@/pages/Disasters";
import DisasterPage from "@/pages/DisasterPage";
import NewsWire from "@/pages/NewsWire";
import PlacePage from "@/pages/PlacePage";
import MapPage from "@/pages/MapPage";
import Legal from "@/pages/Legal";
import A11yBar from "@/components/site/A11yBar";
import { LocationProvider } from "@/lib/LocationContext";
import { GrandparentProvider } from "@/lib/GrandparentContext";
import { useLenis } from "@/lib/lenis";

// App (authenticated product)
import { AuthProvider } from "@/lib/AuthContext";
import { ThemeProvider } from "@/lib/ThemeContext";
import AppShell from "@/components/app/AppShell";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Login from "@/pages/app/Login";
import Register from "@/pages/app/Register";
import Dashboard from "@/pages/app/Dashboard";
import FamilyWorkspace from "@/pages/app/FamilyWorkspace";
import FamilyFeed from "@/pages/app/FamilyFeed";
import MemorialPage from "@/pages/app/MemorialPage";
import Journal from "@/pages/app/Journal";
import Interview from "@/pages/app/Interview";
import Council from "@/pages/app/Council";
import MyCases from "@/pages/app/MyCases";
import Moderation from "@/pages/app/Moderation";
import SsoRollout from "@/pages/app/SsoRollout";
import SocialPulseCallback from "@/pages/app/SocialPulseCallback";

function MarketingShell({ children }) {
  useLenis();
  return (
    <div className="grain relative min-h-screen bg-[color:var(--bone)] text-[color:var(--ink)]">
      <Nav />
      {children}
      <Footer />
    </div>
  );
}

function RootRouter() {
  const location = useLocation();
  const isApp = location.pathname === "/app" || location.pathname.startsWith("/app/");

  if (isApp) {
    return (
      <AppShell>
        <Routes>
          <Route path="/app/login" element={<Login />} />
          <Route path="/app/register" element={<Register />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/family/:id"
            element={
              <ProtectedRoute>
                <FamilyWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/family/:id/feed"
            element={
              <ProtectedRoute>
                <FamilyFeed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/family/:id/council"
            element={
              <ProtectedRoute>
                <Council />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/memorial/:personId"
            element={
              <ProtectedRoute>
                <MemorialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/journal"
            element={
              <ProtectedRoute>
                <Journal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/interview"
            element={
              <ProtectedRoute>
                <Interview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/missing"
            element={
              <ProtectedRoute>
                <MyCases />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/moderation"
            element={
              <ProtectedRoute>
                <Moderation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/sso-rollout"
            element={
              <ProtectedRoute>
                <SsoRollout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AppShell>
    );
  }

  return (
    <MarketingShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/local" element={<Local />} />
        <Route path="/missing" element={<MissingDirectory />} />
        <Route path="/missing/case/:caseId" element={<MissingCase />} />
        <Route path="/missing/report" element={<ReportMissing />} />
        <Route path="/disasters" element={<Disasters />} />
        <Route path="/disasters/:id" element={<DisasterPage />} />
        <Route path="/news" element={<NewsWire />} />
        <Route path="/places/:slug" element={<PlacePage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/privacy" element={<Legal kind="privacy" />} />
        <Route path="/terms" element={<Legal kind="terms" />} />
        <Route path="/memorial/:id" element={<PublicMemorial />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/celebrate-a-life" element={<CelebrateALifePage />} />
        <Route path="/family-trees" element={<FamilyTrees />} />
        <Route path="/culture" element={<Culture />} />
        <Route path="/category/heritage" element={<Heritage />} />
        <Route path="/category/genealogy" element={<Genealogy />} />
        <Route path="/category/memorials" element={<Memorials />} />
        <Route path="/category/:slug" element={<Category />} />
        <Route path="/cemeteries" element={<Cemeteries />} />
        <Route path="/region/:code" element={<Region />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </MarketingShell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <GrandparentProvider>
        <LocationProvider>
          <RootRouter />
          <A11yBar />
        <Toaster
          position="bottom-right"
          theme="light"
          toastOptions={{
            style: {
              fontFamily: "Outfit, sans-serif",
              border: "1px solid rgba(26,26,26,0.25)",
              background: "#F4F1EC",
              color: "#1A1A1A",
              borderRadius: 0,
            },
          }}
        />
        </LocationProvider>
        </GrandparentProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
